import { ConsentManagerBuilder } from "@ht-sdks/consent-manager";

import { useCallback, useState, useEffect, useRef } from "react";
import { locale } from "src/config/app-config";
import {
  useDisplayConsentManager,
  useAppActions,
} from "src/hooks/stores/use-app-store";
import {
  CONSENT_NOT_REQUIRED_LOCALES,
  isAdvertisingCategory,
  isFunctionalCategory,
} from "./utils";
import Cookies from "js-cookie";
import { useSegment } from "src/hooks/useSegment";
import { Overlay } from "src/atoms/Overlay";
import {
  Destination,
  CategoryPreferences,
} from "@ht-sdks/consent-manager/types/types";
import ConsentManagerDialog from "./ConsentManagerDialog/ConsentManagerDialog";
import { usePageViewedDispatch } from "src/hooks/context/use-page-viewed-context";
import { isNativeApp } from "src/utils/isNativeApp";
import { useRouter } from "next/router";
import { initializeGTMConsent } from "src/tracking/googleTagManager";

export const COOKIE_NAME = "otrium_cookie-tracking-preferences-v4";

function gtag() {
  if (isNativeApp) return;
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

export const handleMapCustomPreferences = (
  destinations: Destination[],
  preferences: CategoryPreferences
): {
  destinationPreferences: CategoryPreferences;
  customPreferences: CategoryPreferences;
} => {
  const customPreferences: CategoryPreferences = {};
  const destinationPreferences: CategoryPreferences = {};

  // Default unset preferences to true (for implicit consent)
  for (const preferenceName of Object.keys(preferences)) {
    const value = preferences[preferenceName];
    const convertedValue = typeof value === "boolean" ? value : true;

    customPreferences[preferenceName] = convertedValue;
  }

  for (const destination of destinations) {
    // Mark advertising destinations
    if (
      isAdvertisingCategory(destination.category) &&
      destinationPreferences[destination.id] !== false
    ) {
      destinationPreferences[destination.id] = customPreferences.advertising;
    }

    // Mark function destinations
    if (
      isFunctionalCategory(destination.category) &&
      destinationPreferences[destination.id] !== false
    ) {
      destinationPreferences[destination.id] = customPreferences.functional;
    }

    // Fallback to marketing
    if (!(destination.id in destinationPreferences)) {
      destinationPreferences[destination.id] = customPreferences.marketing;
    }
  }

  return { destinationPreferences, customPreferences };
};

export const ConsentManagerBanner = (): JSX.Element | null => {
  const displayConsentManager = useDisplayConsentManager();
  const { setConsentUpdated } = useAppActions();
  const { consentStatusUpdated } = useSegment();
  const [consentAccepted, setConsentAccepted] = useState(false);
  const pageViewedDispatch = usePageViewedDispatch();
  const router = useRouter();
  const consentInitializedRef = useRef(false);

  const shouldRequireConsent = useCallback((): boolean => {
    const storeRequiresConsent = !CONSENT_NOT_REQUIRED_LOCALES.includes(locale);
    const isIOSApp =
      typeof webkit !== "undefined" &&
      typeof (webkit as { messageHandlers?: { track?: any } }).messageHandlers
        ?.track !== "undefined";

    const isAndroidApp = typeof segment_webview !== "undefined";
    return (
      storeRequiresConsent &&
      displayConsentManager &&
      !(isIOSApp || isAndroidApp)
    );
  }, [displayConsentManager]);

  const updateConsentOnGTM = useCallback(
    (newPreferences: CategoryPreferences, onSave?: boolean): void => {
      if (typeof gtag === "function") {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        gtag("consent", "update", {
          ad_user_data: newPreferences.marketing ? "granted" : "denied",
          ad_personalization: newPreferences.marketing ? "granted" : "denied",
          ad_storage: newPreferences.marketing ? "granted" : "denied",
          analytics_storage: newPreferences.analytics ? "granted" : "denied",
        });

        initializeGTMConsent();

        setConsentUpdated(true);

        setConsentAccepted(
          !!(newPreferences?.marketing && newPreferences?.analytics)
        );

        if (onSave) {
          void consentStatusUpdated(newPreferences);
          pageViewedDispatch({
            type: "SET_CONSENT_STATUS",
            payload: true,
          });
        }
      } else {
        console.error("gtag function is not available.");
      }
    },
    [consentStatusUpdated, pageViewedDispatch, setConsentUpdated]
  );

  /**
   * Set the default consent preferences on Google Tag Manager
   * This function will push a dataLayer event to set the default consent preferences
   * With that, we can start tracking the user's consent preferences in Google Tag Manager
   * If any consent is given before, this function will read it from cookie
   * and update the consent preferences on Google Tag Manager.
   */
  const setDefaultConsentOnGTM = useCallback((): void => {
    if (consentInitializedRef.current) return;
    if (!window.dataLayer) return;

    const cookiePreferences = Cookies.getJSON(COOKIE_NAME) as
      | { custom?: CategoryPreferences }
      | undefined;

    if (cookiePreferences && cookiePreferences.custom) {
      updateConsentOnGTM(cookiePreferences.custom);
      consentInitializedRef.current = true;
      return;
    }

    const consentRequired = shouldRequireConsent();
    if (!consentRequired) {
      initializeGTMConsent();
      consentInitializedRef.current = true;
    }
  }, [updateConsentOnGTM, shouldRequireConsent]);

  /**
   * Wrapper function to add Segment tracking to the saveConsent function
   * This function will push a dataLayer event to track the user's consent preferences
   * With that, we can track the user's consent preferences in Google Tag Manager
   * @param saveConsent - The saveConsent function from the ConsentManagerBuilder
   * @param newPreferences - The new preferences to save
   * @param shouldReload - Whether the page should be reloaded after saving the preferences
   * @return void
   */
  const saveConsentWrapper = (
    saveConsent: (
      newPreferences?: CategoryPreferences | boolean,
      shouldReload?: boolean
    ) => void,
    newPreferences?: CategoryPreferences | boolean,
    shouldReload?: boolean
  ): void => {
    saveConsent(newPreferences, shouldReload);

    if (
      typeof newPreferences !== "boolean" &&
      newPreferences &&
      window.dataLayer
    ) {
      const onSave = true;
      updateConsentOnGTM(newPreferences, onSave);
    }
  };

  useEffect(() => {
    setDefaultConsentOnGTM();
  }, [setDefaultConsentOnGTM]);

  useEffect(() => {
    if (displayConsentManager) {
      window.scrollTo(0, 0);
    }
  }, [displayConsentManager]);

  useEffect(() => {
    const currentValue = Cookies.get(COOKIE_NAME);

    if (currentValue !== undefined) {
      Cookies.remove(COOKIE_NAME);
      Cookies.set(COOKIE_NAME, currentValue, { expires: 365 });
    }
  }, [consentAccepted]);

  if (
    !process.env.HIGHTOUCH_WRITE_KEY ||
    isNativeApp ||
    "app" in router.query ||
    router.route === "/app-login"
  ) {
    return null;
  }

  return (
    <ConsentManagerBuilder
      cookieName={COOKIE_NAME}
      cookieExpires={14}
      writeKey={process.env.HIGHTOUCH_WRITE_KEY}
      options={{
        apiHost: "eu-west-1.hightouch-events.com",
      }}
      shouldRequireConsent={shouldRequireConsent}
      mapCustomPreferences={handleMapCustomPreferences}
    >
      {({
        destinations,
        preferences,
        isConsentRequired,
        setPreferences,
        saveConsent,
        resetPreferences,
      }) => {
        return displayConsentManager && isConsentRequired ? (
          <>
            <Overlay isOpen={true} />
            <ConsentManagerDialog
              destinations={destinations}
              preferences={preferences}
              setPreferences={setPreferences}
              saveConsent={(newPreferences, shouldReload) => {
                saveConsentWrapper(saveConsent, newPreferences, shouldReload);
              }}
              resetPreferences={resetPreferences}
            />
          </>
        ) : (
          // Returning a fragment because `null` doesn't pass typechecking
          <></>
        );
      }}
    </ConsentManagerBuilder>
  );
};
